/**
 * Shared timezone-aware date/time helpers.
 *
 * INVARIANT: user-facing timestamps are always rendered in the viewer's
 * stored timezone (usually pros.timezone or users.timezone from the
 * app's user context). Do NOT reach for the browser Date "toLocale"
 * formatters without an explicit timeZone option — they silently fall
 * back to the browser's tz and that's the source of the "9:30 PDT
 * looks like 9:30 EDT" bug class.
 *
 * Use these helpers everywhere date/times are shown to a user, passing
 * the viewer's timezone explicitly.
 *
 * WHICH FUNCTION WHEN:
 *  - Have UTC instant, want to display a string    → formatInTz
 *  - Have UTC instant, want tz-scoped arithmetic
 *    (`.getDay()` / `.getHours()` in a specific tz) → zonedDate
 *  - Have naive wall-time string, want UTC instant → parseInZone
 *  - Have `tz: string | null`, want a resolved tz  → resolveTz
 *
 * TZDATA SOURCE: offset lookup goes through `moment-timezone` (bundled,
 * updatable via `pnpm update moment-timezone`) instead of Node's ICU.
 * Node's ICU tzdata trails IANA releases by months — e.g. BC's
 * 2026-03-08, AB's 2026-11-01, and MB's 2026-11-01 permanent-DST
 * changes aren't in Node 22 LTS as of 2026-11. moment-timezone 0.6.6
 * (tzdata 2026f) has all three, and its offset numbers drive our
 * formatting so we're not blocked on the Node → Vercel deploy cadence.
 */
import moment from 'moment-timezone'
// eslint-disable-next-line no-restricted-imports -- packages/time is the sanctioned wrapper (see file header)
import { formatInTimeZone } from 'date-fns-tz'
// eslint-disable-next-line no-restricted-imports -- type-only, same sanction
import type { FormatOptionsWithTZ } from 'date-fns-tz'

/** Coerce Date | ISO string into a UTC-suffixed ISO string. Naive strings
 *  (no offset, no Z) are assumed to be UTC — matches how our DB timestamps
 *  land in JSON. Exported so callers that render with `toLocaleXxxString`
 *  (rather than `formatInTz`) can still hit the same parse rule. */
export function asUtcInput(input: Date | string): string | Date {
  if (typeof input !== 'string') return input
  if (input.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(input)) return input
  return input + 'Z'
}

/** Parse a Date | ISO string as UTC. Convenience for callers that need
 *  a Date object (e.g. arithmetic like `.getTime()` for minutes-until). */
export function parseAsUtc(input: Date | string): Date {
  const coerced = asUtcInput(input)
  return typeof coerced === 'string' ? new Date(coerced) : coerced
}

/**
 * Resolve a possibly-null tz into a definite IANA name. Falls back to
 * the browser/runtime's zone when `tz` is null/empty. Kept small so
 * callers with `pros.timezone | null` don't have to repeat the pattern.
 */
export function resolveTz(tz: string | null | undefined): string {
  return tz || Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Format a UTC timestamp in the given timezone using date-fns pattern
 * tokens (e.g. "yyyy-MM-dd", "h:mm a zzz", "EEE, MMM d"). Never uses
 * browser tz.
 *
 * Offset source is moment-timezone, not Node ICU. See file header.
 *
 * `options` matches date-fns-tz `formatInTimeZone`'s 4th arg — pass
 * `{ locale }` for i18n, etc. Passed straight through to date-fns.
 *
 * IMPLEMENTATION: shift the instant by the tz's utcOffset, then format
 * as UTC — this makes wall-time tokens (H h m d M y E …) render for the
 * target tz without going through Intl.
 *
 * Zone tokens (z zz zzz zzzz X XX XXX XXXX XXXXX x xx xxx xxxx xxxxx
 * O OO OOO OOOO) need special handling: date-fns-tz resolves them from
 * the `timeZone` arg (which is 'UTC' in our shifted-format trick), so
 * left alone they'd all print "UTC" / "Z" / "GMT+0". We preprocess the
 * pattern to replace unquoted zone tokens with quoted literal strings
 * computed from the real tz + moment-timezone offset before handing off
 * to date-fns-tz.
 */
export function formatInTz(
  input: Date | string,
  tz: string,
  pattern: string,
  options?: FormatOptionsWithTZ,
): string {
  const utc = parseAsUtc(input)
  const offsetMinutes = moment.tz(utc, tz).utcOffset()
  const shifted = new Date(utc.getTime() + offsetMinutes * 60000)
  const rewritten = rewriteZoneTokens(pattern, tz, utc, offsetMinutes)
  return formatInTimeZone(shifted, 'UTC', rewritten, options)
}

/**
 * Replace unquoted zone tokens in a date-fns pattern with pre-computed
 * quoted literals. Preserves date-fns quoting rules: `'text'` is a
 * literal, `''` inside a quoted section is an escaped single quote.
 */
function rewriteZoneTokens(
  pattern: string,
  tz: string,
  utc: Date,
  offsetMinutes: number,
): string {
  const sign = offsetMinutes < 0 ? '-' : '+'
  const absMin = Math.abs(offsetMinutes)
  const hh = String(Math.floor(absMin / 60)).padStart(2, '0')
  const mm = String(absMin % 60).padStart(2, '0')
  const isZero = offsetMinutes === 0
  const zoneAbbr = formatTzAbbrev(utc, tz)
  const hhCompact = String(Math.floor(absMin / 60))
  const mmSuffix = absMin % 60 === 0 ? '' : `:${mm}`

  const literalFor = (token: string): string | null => {
    if (token === 'z' || token === 'zz' || token === 'zzz') return zoneAbbr
    // No accurate long-form ("Eastern Daylight Time") available without
    // Intl or a bundled name table — fall back to the abbreviation.
    if (token === 'zzzz') return zoneAbbr
    if (token === 'X') return isZero ? 'Z' : `${sign}${hh}`
    if (token === 'XX') return isZero ? 'Z' : `${sign}${hh}${mm}`
    if (token === 'XXX') return isZero ? 'Z' : `${sign}${hh}:${mm}`
    if (token === 'XXXX') return `${sign}${hh}${mm}`
    if (token === 'XXXXX') return `${sign}${hh}:${mm}`
    if (token === 'x') return `${sign}${hh}`
    if (token === 'xx') return `${sign}${hh}${mm}`
    if (token === 'xxx') return `${sign}${hh}:${mm}`
    if (token === 'xxxx') return `${sign}${hh}${mm}`
    if (token === 'xxxxx') return `${sign}${hh}:${mm}`
    if (token === 'O' || token === 'OO' || token === 'OOO') return `GMT${sign}${hhCompact}${mmSuffix}`
    if (token === 'OOOO') return `GMT${sign}${hh}:${mm}`
    return null
  }

  let out = ''
  let i = 0
  while (i < pattern.length) {
    const ch = pattern[i]!
    if (ch === "'") {
      // Copy quoted section verbatim, including escaped `''`.
      out += ch
      i++
      while (i < pattern.length) {
        out += pattern[i]!
        if (pattern[i] === "'") {
          if (pattern[i + 1] === "'") {
            i++
            out += pattern[i]!
          } else {
            i++
            break
          }
        }
        i++
      }
      continue
    }
    if (ch === 'z' || ch === 'X' || ch === 'x' || ch === 'O') {
      let j = i
      while (j < pattern.length && pattern[j] === ch) j++
      const token = pattern.slice(i, j)
      const literal = literalFor(token)
      if (literal !== null) {
        out += "'" + literal.replace(/'/g, "''") + "'"
        i = j
        continue
      }
    }
    out += ch
    i++
  }
  return out
}

/**
 * Drop-in replacement for date-fns-tz `toZonedTime`. Takes a UTC instant
 * and returns a Date such that reading it with LOCAL accessors
 * (`.getHours()`, `.getDate()`, `.getDay()`, …) yields the wall-time
 * components as seen in `tz`. Callers typically use the result for
 * tz-scoped arithmetic like day-of-week bucketing or hour extraction.
 *
 * The offset comes from moment-timezone's bundled tzdata, so BC/AB
 * permanent-DST changes are respected regardless of Node ICU.
 *
 * IMPLEMENTATION: shift the UTC timestamp by (targetTzOffset - localTzOffset)
 * so that local accessors — which subtract the runtime's local offset —
 * end up returning the target-tz wall clock. Matches date-fns-tz v3
 * semantics on both UTC (Vercel functions) and non-UTC (local dev)
 * runtimes.
 */
export function zonedDate(input: Date | string, tz: string): Date {
  const utc = parseAsUtc(input)
  const zoneOffsetMinutes = moment.tz(utc, tz).utcOffset()
  // Date.getTimezoneOffset() returns UTC-minus-local in minutes (positive
  // west of UTC). Flip the sign to get the signed local offset.
  const localOffsetMinutes = -utc.getTimezoneOffset()
  const shiftMinutes = zoneOffsetMinutes - localOffsetMinutes
  return new Date(utc.getTime() + shiftMinutes * 60000)
}

/**
 * Drop-in replacement for date-fns-tz `fromZonedTime`. Interprets a
 * naive wall-time as being in `tz`, returns the UTC instant.
 *
 * Input semantics — matches date-fns-tz v3 exactly:
 *  - String: any trailing "Z" or "±HH:MM" / "±HHMM" is stripped. The
 *    remaining YYYY-MM-DD[THH:MM:SS[.SSS]] is treated as wall time in
 *    `tz`. Callers who want the offset to matter should convert to a
 *    Date first (via `new Date(...)`) and use `parseAsUtc` / other
 *    helpers.
 *  - Date: the LOCAL fields (`getFullYear`, `getHours`, …) hold the
 *    wall-time components; they get interpreted in `tz`. On the UTC
 *    runtimes Vercel functions use, local == UTC methods, but on
 *    non-UTC dev machines the distinction matters.
 *
 * Ambiguous / non-existent wall times around DST transitions resolve to
 * moment-timezone's default (post-transition offset). Our data doesn't
 * schedule inside the spring-forward gap so this shouldn't bite.
 */
export function parseInZone(wall: string | Date, tz: string): Date {
  if (wall instanceof Date) {
    return moment
      .tz(
        [
          wall.getFullYear(),
          wall.getMonth(),
          wall.getDate(),
          wall.getHours(),
          wall.getMinutes(),
          wall.getSeconds(),
          wall.getMilliseconds(),
        ],
        tz,
      )
      .toDate()
  }
  // Strip trailing tz suffix (Z, ±HH:MM, or ±HHMM) — caller wants the
  // remaining wall-clock components interpreted as local to `tz`.
  const naive = wall.replace(/(?:Z|[+-]\d{2}:?\d{2})$/, '')
  return moment.tz(naive, tz).toDate()
}

/** "Sep 21, 2026" */
export function formatDateShort(input: Date | string, tz: string): string {
  return formatInTz(input, tz, 'MMM d, yyyy')
}

/** "Sep 21" */
export function formatDateNoYear(input: Date | string, tz: string): string {
  return formatInTz(input, tz, 'MMM d')
}

/** "Monday, September 21" */
export function formatDateLong(input: Date | string, tz: string): string {
  return formatInTz(input, tz, 'EEEE, MMMM d')
}

/** "Mon, Sep 21" */
export function formatDateWeekday(input: Date | string, tz: string): string {
  return formatInTz(input, tz, 'EEE, MMM d')
}

/** "9:30 AM" */
export function formatTime(input: Date | string, tz: string): string {
  return formatInTz(input, tz, 'h:mm a')
}

/** "9am" / "9:30am" (lowercase, terse — for casual UI copy). */
export function formatTimeCasual(input: Date | string, tz: string): string {
  const raw = formatInTz(input, tz, 'h:mma')
  // date-fns emits "AM"/"PM"; lowercase for the casual form.
  return raw.replace(/AM$/, 'am').replace(/PM$/, 'pm')
}

/** "9:30 AM – 10:30 AM" — safe for cross-day-boundary ranges too. */
export function formatTimeRange(
  startInput: Date | string,
  endInput: Date | string,
  tz: string,
): string {
  return `${formatTime(startInput, tz)} – ${formatTime(endInput, tz)}`
}

/** "Sep 21, 2026 · 9:30 AM" — the everyday "show me this appointment" line. */
export function formatDateTime(input: Date | string, tz: string): string {
  return `${formatDateShort(input, tz)} · ${formatTime(input, tz)}`
}

/**
 * Timezone abbreviation ("EDT" / "MST" / "PT", DST-aware) for a given
 * moment in the given tz. Used for "9:30 AM ET"-style labels.
 *
 * BC (2026-03-08), AB (2026-11-01), and MB (2026-11-01) rebranded to
 * just "Pacific Time" (PT), "Alberta Time" (displayed as "MT"), and
 * "Central Time" (CT) — no more PST/PDT, MST/MDT, or CST/CDT swap.
 * Note Manitoba's permanent offset is UTC-5 (same as EST numerically)
 * but the province still calls it Central Time, so we display "CT".
 * These aren't stale-ICU workarounds; they're the government's chosen
 * display labels.
 */
export function formatTzAbbrev(input: Date | string, tz: string): string {
  if (tz === 'America/Vancouver') return 'PT'
  if (tz === 'America/Edmonton') return 'MT'
  if (tz === 'America/Winnipeg') return 'CT'
  try {
    const inst = new Date(asUtcInput(input) as string | Date)
    // eslint-disable-next-line no-restricted-syntax -- @repo/time is the sanctioned owner of tz-aware Intl usage
    const shortParts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    }).formatToParts(inst)
    const shortLabel =
      shortParts.find(p => p.type === 'timeZoneName')?.value ?? ''

    // `short` returns "GMT+8" / "GMT" for zones ICU can't abbreviate
    // (most non-US/EU zones). That's unreadable, so fall back to the
    // `long` form ("Central Indonesia Time") which Intl always has a
    // friendly label for. If even that's GMT-based (very rare — happens
    // for the raw Etc/GMT zones), swap the "GMT" prefix for "UTC" so we
    // at least don't ship the ancient "GMT" marker to users.
    if (/^GMT([+\-−]|$)/.test(shortLabel)) {
      // eslint-disable-next-line no-restricted-syntax -- see above
      const longParts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'long',
      }).formatToParts(inst)
      const longLabel =
        longParts.find(p => p.type === 'timeZoneName')?.value ?? ''
      if (longLabel && !/^GMT([+\-−]|$)/.test(longLabel)) return longLabel
      return shortLabel.replace(/^GMT/, 'UTC')
    }
    return shortLabel
  } catch {
    return ''
  }
}

/** "yyyy-MM-dd" — machine-oriented day key in the given tz. Used for
 *  grouping instances by day when the day boundary must respect the
 *  viewer's tz rather than the browser's. */
export function formatDayKey(input: Date | string, tz: string): string {
  return formatInTz(input, tz, 'yyyy-MM-dd')
}
